import { Link, useOutletContext, useParams } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/solid';

import AnalyticsTable from '../../../components/AiSpend/AnalyticsTable';
import UsageCell from '../../../components/AiSpend/UsageCell';
import ErrorState from '../../../components/DataDisplay/ErrorState';
import StatCard from '../../../components/DataDisplay/StatCard';
import { getAiSpendUserDetail } from '../../../services/aiSpendService';
import {
  formatCount,
  formatInstant,
  formatMoney,
} from '../../../utils/helpers/aiSpendFormat';
import useApiResource from '../../../utils/hooks/useApiResource';
import { AI_SPEND_BASE } from './aiSpendPages';

const breakdownColumns = [
  { key: 'label', label: 'Name', render: (row) => row.label },
  { key: 'cost_usd', label: 'Cost', render: (row) => formatMoney(row.cost_usd) },
  { key: 'calls', label: 'Calls', render: (row) => formatCount(row.calls) },
];

export default function AiSpendUserDetail() {
  const { subjectUuid } = useParams();
  const { queryParams, queryKey, options } = useOutletContext();
  const { data, error, loading, refetch } = useApiResource(
    ({ signal }) => getAiSpendUserDetail({ subjectUuid, params: queryParams, signal }),
    { deps: [`${subjectUuid}:${queryKey}`] },
  );

  if (error) return <ErrorState message={error} onRetry={refetch} />;
  const totals = data?.totals || {};

  return (
    <div className="space-y-6">
      <Link
        to={{ pathname: `${AI_SPEND_BASE}/users`, search: window.location.search }}
        className="inline-flex items-center gap-2 text-sm font-semibold text-blue-600 hover:text-blue-800"
      >
        <ArrowLeftIcon className="h-4 w-4" />
        All students
      </Link>
      <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
        {data?.email || 'Student'}
      </h2>

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Total spend"
          value={formatMoney(totals.cost_usd)}
          rawValue={totals.cost_usd}
          loading={loading}
        />
        <StatCard
          label="Calls"
          value={formatCount(totals.calls)}
          rawValue={totals.calls}
          loading={loading}
        />
        <StatCard
          label="Failed calls"
          value={formatCount(totals.failed_calls)}
          rawValue={totals.failed_calls}
          loading={loading}
        />
        <StatCard
          label="Sessions"
          value={formatCount(totals.distinct_sessions)}
          rawValue={totals.distinct_sessions}
          loading={loading}
        />
      </section>

      <div className="grid gap-6 lg:grid-cols-3">
        <section>
          <h3 className="mb-2 font-semibold text-slate-900 dark:text-white">By feature</h3>
          <AnalyticsTable loading={loading} rows={data?.by_feature || []} columns={breakdownColumns} />
        </section>
        <section>
          <h3 className="mb-2 font-semibold text-slate-900 dark:text-white">By provider</h3>
          <AnalyticsTable loading={loading} rows={data?.by_provider || []} columns={breakdownColumns} />
        </section>
        <section>
          <h3 className="mb-2 font-semibold text-slate-900 dark:text-white">By model</h3>
          <AnalyticsTable loading={loading} rows={data?.by_model || []} columns={breakdownColumns} />
        </section>
      </div>

      <section>
        <h3 className="mb-2 font-semibold text-slate-900 dark:text-white">Recent calls</h3>
        <AnalyticsTable
          loading={loading}
          rows={data?.recent_runs || []}
          rowKey={(row) => row.uuid}
          columns={[
            {
              key: 'created',
              label: 'When',
              render: (row) => formatInstant(row.created, options?.timezone),
            },
            { key: 'feature_label', label: 'Feature', render: (row) => row.feature_label },
            { key: 'model', label: 'Model', render: (row) => row.model },
            {
              key: 'usage',
              label: 'Usage',
              render: (row) => <UsageCell usage={row.usage} billingUnit={row.billing_unit} />,
            },
            { key: 'cost_usd', label: 'Cost', render: (row) => formatMoney(row.cost_usd) },
          ]}
        />
      </section>
    </div>
  );
}
